import {
  messages,
  ZStream,
  zlibInflateInit2,
  zlibInflateSetDictionary,
  zlibInflate,
  zlibInflateReset,
  zlibInflateEnd
} from './zlib.mjs';
import type { Z_CallStatus, Z_FlushMode } from './zlib.mjs';
import { flattenChunks } from './utils.ts';

const toString = Object.prototype.toString;

/* Public constants ==========================================================*/
/* ===========================================================================*/

import {
  Z_NO_FLUSH, Z_FINISH,
  Z_OK, Z_STREAM_END, Z_NEED_DICT, Z_STREAM_ERROR, Z_DATA_ERROR, Z_MEM_ERROR, Z_BUF_ERROR
} from './zlib/constants.mjs';

/* ===========================================================================*/

/** @inline */
type InflateInput = Uint8Array | ArrayBuffer;

interface InflateOptions {
  /**
   * Window size. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  windowBits?: number;
  /**
   * Initial dictionary. See the
   * [zlib manual](http://zlib.net/manual.html#Advanced) for more information.
   * @group zlib options
   */
  dictionary?: Uint8Array | ArrayBuffer;
  /**
   * Size of generated data chunks (64K by default).
   * @group Extensions
   */
  chunkSize?: number;
  /**
   * Do raw inflate. Say that we work with raw stream, if you don't wish to
   * specify negative `windowBits` implicitly.
   * @group Extensions
   */
  raw?: boolean;
}

const defaultOptions: Required<InflateOptions> = {
  chunkSize: 1024 * 64,
  windowBits: 15,
  raw: false,
  dictionary: new Uint8Array(0)
};

/**
 * Generic JS-style wrapper for zlib calls. If you don't need
 * streaming behaviour, use the simpler functions {@link inflate}
 * and {@link inflateRaw}.
 */
class Inflate {
  private options: Required<InflateOptions>;

  /**
   * Error code after inflate finishes. {@link Z_OK} on success.
   * Should be checked when broken data is possible.
   */
  err: Z_CallStatus;

  /** Error message, if {@link Inflate.err} is not {@link Z_OK}. */
  msg: string;

  /**
   * `true` once the compressed stream has ended. A stream may end before the
   * caller's data does (trailing bytes), so check this to know when to stop
   * pushing - further {@link Inflate.push} calls are no-ops.
   */
  ended: boolean;

  private started: boolean;

  /**
   * Chunks of output data, if {@link Inflate.onData} not overridden.
   * @internal
   */
  chunks: Uint8Array<ArrayBuffer>[];

  private strm: ZStream;

  /**
   * Uncompressed result, generated by default {@link Inflate.onData}
   * and {@link Inflate.onEnd} handlers. Filled after you push last chunk
   * (call {@link Inflate.push} with {@link Z_FINISH} / `true` param).
   */
  result: Uint8Array<ArrayBuffer>;

  /**
   * Creates a new inflator instance with the specified params. Throws an
   * exception on bad params. See {@link InflateOptions} for the list of
   * supported options.
   *
   * By default, when no options are set, the deflate/gzip data format is
   * autodetected via the wrapper header.
   *
   * @example
   * ```javascript
   * import { Inflate } from 'pako'
   *
   * const chunk1 = new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9])
   * const chunk2 = new Uint8Array([10, 11, 12, 13, 14, 15, 16, 17, 18, 19])
   *
   * const inflate = new Inflate({ level: 3 })
   *
   * inflate.push(chunk1, false)
   * inflate.push(chunk2, true)  // true -> last chunk
   *
   * if (inflate.err) throw new Error(inflate.err)
   *
   * console.log(inflate.result)
   * ```
   */
  constructor(options: InflateOptions = {}) {
    this.options = Object.assign({}, defaultOptions, options);

    const opt = this.options;

    // Force window size for `raw` data, if not set directly,
    // because we have no header for autodetect.
    if (opt.raw && (opt.windowBits >= 0) && (opt.windowBits < 16)) {
      opt.windowBits = -opt.windowBits;
      if (opt.windowBits === 0) { opt.windowBits = -15; }
    }

    // If `windowBits` not defined (and mode not raw) - set autodetect flag for gzip/deflate
    if ((opt.windowBits >= 0) && (opt.windowBits < 16) &&
        !options.windowBits) {
      opt.windowBits += 32;
    }

    // Gzip header has no info about windows size, we can do autodetect only
    // for deflate. So, if window size not set, force it to max when gzip possible
    if ((opt.windowBits > 15) && (opt.windowBits < 48)) {
      // bit 3 (16) -> gzipped data
      // bit 4 (32) -> autodetect gzip/deflate
      if ((opt.windowBits & 15) === 0) {
        opt.windowBits |= 15;
      }
    }

    this.err    = Z_OK;   // error code, if happens
    this.msg    = '';     // error message
    this.ended  = false;  // used to avoid multiple onEnd() calls
    this.started = false; // used to call onStart() only once
    this.chunks = [];     // chunks of compressed data
    this.result = new Uint8Array(0);

    this.strm   = new ZStream();
    this.strm.avail_out = 0;

    let status  = zlibInflateInit2(
      this.strm,
      opt.windowBits
    );

    if (status !== Z_OK) {
      throw new Error(messages[status]);
    }

    if (toString.call(opt.dictionary) === '[object ArrayBuffer]') {
      opt.dictionary = new Uint8Array(opt.dictionary as ArrayBuffer);
    }

    const dictionary = opt.dictionary as Uint8Array;

    if (opt.raw && dictionary.length) {
      status = zlibInflateSetDictionary(this.strm, dictionary);
      if (status !== Z_OK) {
        throw new Error(messages[status]);
      }
    }
  }

  /**
   * Sends input data to the inflate pipe, generating {@link Inflate.onData} calls
   * with new output chunks. Returns `true` on success. If end of stream is
   * detected, {@link Inflate.onEnd} will be called.
   *
   * `flush_mode` is not needed for normal operation, because end of stream
   * is detected automatically. Pass {@link Z_SYNC_FLUSH} to force the decoder
   * to emit all currently available output — handy when you need to decode
   * data frame-by-frame from a long-running stream.
   *
   * On failure, calls {@link Inflate.onEnd} with the error code and returns false.
   *
   * Once the stream has ended (a compressed stream may end before your data
   * does), further `push` calls are no-ops and return whether the decode
   * finished successfully. The final outcome is in {@link Inflate.result},
   * {@link Inflate.err} and {@link Inflate.msg}.
   *
   * @param flush_mode 0..6 for corresponding {@link Z_NO_FLUSH}..{@link Z_TREES}
   *   flush modes. See constants. Skipped or `false` means {@link Z_NO_FLUSH},
   *   `true` means {@link Z_FINISH}.
   *
   * @example
   * ```javascript
   * push(chunk, false) // push one of data chunks
   * ...
   * push(chunk, true)  // push last chunk
   * ```
   */
  push(data: InflateInput, flush_mode: Z_FlushMode | boolean = false): boolean {
    const strm = this.strm;
    const chunkSize = this.options.chunkSize;
    let status: Z_CallStatus;
    let _flush_mode: Z_FlushMode;
    let last_avail_out: number;

    // Once the stream has ended, further pushes are no-ops. A compressed stream
    // can end before the caller's data does (trailing bytes), and the caller has
    // no way to know that in advance - so report the recorded outcome rather than
    // a blanket `false`: a successful decode keeps returning `true` while trailing
    // chunks are fed, errors keep returning `false`. `err`/`msg`/`result` are
    // left intact, so the delivered result survives any trailing garbage.
    if (this.ended) return this.err === Z_OK;

    if (typeof flush_mode === 'number') _flush_mode = flush_mode;
    else _flush_mode = flush_mode === true ? Z_FINISH : Z_NO_FLUSH;

    // Convert data if needed
    if (toString.call(data) === '[object ArrayBuffer]') {
      strm.input = new Uint8Array(data as ArrayBuffer);
    } else {
      strm.input = data as Uint8Array;
    }

    strm.next_in = 0;
    strm.avail_in = strm.input.length;

    if (!this.started) {
      this.started = true;
      this.onStart(strm);
    }

    for (;;) {
      if (strm.avail_out === 0) {
        strm.output = new Uint8Array(chunkSize);
        strm.next_out = 0;
        strm.avail_out = chunkSize;
      }

      status = zlibInflate(strm, _flush_mode);

      if (status === Z_NEED_DICT) {
        const dictionary = this.options.dictionary as Uint8Array;

        if (dictionary.length) {
          status = zlibInflateSetDictionary(strm, dictionary);

          if (status === Z_OK) {
            status = zlibInflate(strm, _flush_mode);
          } else if (status === Z_DATA_ERROR) {
            // Replace code with more verbose
            status = Z_NEED_DICT;
          }
        }
      }

      // Only the gzip format defines concatenated members (RFC 1952: a gzip file
      // is "a series of members"). A zlib stream (RFC 1950) ends after its
      // ADLER32, and a raw DEFLATE stream (RFC 1951) ends after its final block -
      // neither format allows anything to follow, so bytes after the end are not
      // ours to interpret and must be left in the input. Restart decoding only
      // for a gzip member: `state.flags` is non-zero only once a gzip header has
      // actually been decoded (it stays 0 for a zlib member, even when the format
      // was auto-detected and the gzip bit of `wrap` is set). A trailing zero
      // byte is padding, not the start of a member (no member can begin with 0).
      while (strm.avail_in > 0 &&
             status === Z_STREAM_END &&
             (strm.state.wrap & 2) && (strm.state as any).flags !== 0 &&
             strm.input[strm.next_in] !== 0)
      {
        zlibInflateReset(strm);
        status = zlibInflate(strm, _flush_mode);
      }

      // Hard errors. Leave the stream as-is (no inflateEnd) and finalize with
      // this status - `break` carries it to the single exit block below.
      if (status === Z_STREAM_ERROR || status === Z_DATA_ERROR ||
          status === Z_NEED_DICT || status === Z_MEM_ERROR) break;

      // Remember real `avail_out` value before output buffer counters are reset.
      last_avail_out = strm.avail_out;

      if (strm.next_out) {
        // Flush output if buffer is full, stream ended, or an explicit flush was
        // requested (e.g. Z_SYNC_FLUSH) - to push out the tail, same as node's zlib.
        if (strm.avail_out === 0 || status === Z_STREAM_END || _flush_mode > 0) {

          this.onData(strm.output.length === strm.next_out ? strm.output : strm.output.subarray(0, strm.next_out));

          // Force a fresh output buffer on next iteration / next push, so the
          // already emitted tail is not sent again.
          strm.avail_out = 0;
          strm.next_out = 0;
        }
      }

      // A full output buffer means there may be more to produce - allocate a new
      // one and call inflate again. The status depends on the flush mode: with
      // Z_NO_FLUSH a full buffer is reported as Z_OK, but with Z_FINISH the same
      // situation is reported as Z_BUF_ERROR ("could not make progress now",
      // non-fatal) even though output is still pending. Both must continue; the
      // distinction that matters is purely "was the output buffer exhausted".
      if ((status === Z_OK || status === Z_BUF_ERROR) && last_avail_out === 0) continue;

      // End of stream reached - finalize with the inflateEnd status.
      if (status === Z_STREAM_END) {
        status = zlibInflateEnd(this.strm);
        break;
      }

      if (strm.avail_in === 0) {
        // Input is exhausted. If the caller declared this the end of the stream
        // (Z_FINISH) but we never saw Z_STREAM_END, the compressed data ended
        // before its terminating marker - i.e. it is truncated/incomplete. That
        // is an error: returning the partial output as success would be
        // indistinguishable from a complete decode, hiding the data loss. Report
        // it via Z_BUF_ERROR. (Reached only when the output buffer still had room
        // - a full buffer is handled by the `continue` above - so this genuinely
        // means "ran out of input", not "ran out of output".)
        if (_flush_mode === Z_FINISH) {
          status = zlibInflateEnd(this.strm);
          if (status === Z_OK) status = Z_BUF_ERROR;
          break;
        }

        // Not the last chunk - the stream is still alive, more input may follow.
        // The only non-finalizing exit: don't touch err/msg/result, don't call
        // onEnd, just report the push was accepted.
        return true;
      }
    }

    this.err = status;
    this.msg = strm.msg || messages[status];
    this.ended = true;
    this.onEnd(status);

    return status === Z_OK;
  }


  /**
   * Called once before the first low-level inflate call.
   *
   * Override this handler to attach low-level inflate state, for example to read
   * gzip header metadata:
   *
   * ```javascript
   * import { Inflate, GZheader, zlibInflateGetHeader } from 'pako'
   *
   * const inflator = new Inflate()
   *
   * inflator.onStart = function (strm) {
   *   this.header = new GZheader()
   *   zlibInflateGetHeader(strm, this.header)
   * }
   *
   * inflator.push(data, true)
   * console.log(inflator.header.name)
   * ```
   */
  onStart(strm: ZStream): void {}


  /**
   * By default, stores data blocks in the {@link Inflate.chunks} property and glues
   * them in {@link Inflate.onEnd}. Override this handler if you need another behaviour.
   *
   * @param chunk output data.
   */
  onData(chunk: Uint8Array<ArrayBuffer>): void {
    this.chunks.push(chunk);
  }


  /**
   * Called after you tell inflate that the input stream is
   * complete ({@link Z_FINISH}). By default, joins the collected {@link Inflate.chunks},
   * frees memory and fills the {@link Inflate.result} property.
   *
   * @param status inflate status. {@link Z_OK} on success, other if not.
   */
  onEnd(status: Z_CallStatus): void {
    // On success - join collected chunks
    if (status === Z_OK) {
      this.result = flattenChunks(this.chunks);
    }
    this.chunks = [];
  }
}


/**
 * One-shot inflate decompress. Autodetects `gzip`/`zlib`
 * format via the wrapper header — so {@link ungzip} is just a convenience alias of
 * this function. See {@link InflateOptions} for zlib options. Set
 * `toText: true` to decode the result as UTF-8 text.
 *
 * @example
 * ```javascript
 * import { deflate, inflate } from 'pako'
 *
 * const input = deflate(new Uint8Array([1, 2, 3, 4, 5, 6, 7, 8, 9]))
 * let output
 *
 * try {
 *   output = inflate(input)
 * } catch (err) {
 *   console.log(err)
 * }
 * ```
 */
function inflate<O extends InflateOptions & { toText?: boolean }>(
  input: InflateInput,
  options: O = {} as O
): O extends { toText: true } ? string : Uint8Array<ArrayBuffer> {
  const inflator = new Inflate(options);

  inflator.push(input, true);

  // That will never happen if you don't cheat with options :)
  if (inflator.err) throw new Error(inflator.msg);

  const result = inflator.result;

  return (options.toText ? new TextDecoder().decode(result) : result) as
    O extends { toText: true } ? string : Uint8Array<ArrayBuffer>;
}


/**
 * The same as {@link inflate}, but consumes raw data without a wrapper
 * (header and adler32 crc).
 */
function inflateRaw<O extends InflateOptions & { toText?: boolean }>(
  input: InflateInput,
  options: O = {} as O
): O extends { toText: true } ? string : Uint8Array<ArrayBuffer> {
  return inflate<O>(input, { ...options, raw: true } as O);
}


export { Inflate, inflate, inflateRaw, inflate as ungzip };
export type { InflateInput, InflateOptions };
